import { Component, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { AppContextService, ApiGatewayService, ActivityMonitorService } from '@drc-eca/eca-components-lib';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { map, switchMap, catchError, mapTo, tap, takeUntil, debounceTime } from 'rxjs/operators';
import { of, Observable, forkJoin, Subject } from 'rxjs';
import { OktaService } from '../../core/services/okta.service';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-logout',
    templateUrl: './logout.component.html',
    styleUrls: ['./logout.component.scss']
})
export class LogoutComponent implements OnInit, OnDestroy {
  private apiGatewayService = inject(ApiGatewayService);
  private appContextService = inject(AppContextService);
  private activityMonitorService = inject(ActivityMonitorService);
  private oktaService = inject(OktaService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private zone = inject(NgZone);

  private destroy = new Subject()

  ngOnInit() {
    const timeout: boolean = this.route.snapshot.queryParams['timeout'] === 'true';

    this.route.params.pipe(
      switchMap(params => this.getProductCode(params)),
      tap(productCode => this.appContextService.setSelectedProductCode(productCode)),
      map(productCode => this.getReturnUrl(productCode)),
      switchMap(returnUrl => this.logout(returnUrl, timeout)),
      tap(() => {
        this.appContextService.setToken(null as any);
        sessionStorage.clear();
      }),
      debounceTime(16),
      takeUntil(this.destroy)
    ).subscribe(async returnUrl => {
      this.navigate(returnUrl, timeout);
    });
  }

  private getProductCode(params: Params) {
    // if product code is specified in the URL, use it
    if (params.productCode) {
      return of(params.productCode);
    } else {
      // else, lookup previously selected product code
      return this.appContextService.getSelectedProductCode().pipe(
        map(productCode => productCode)
      );
    }
  }

  private getReturnUrl(productCode: string) {
    return productCode ? ['login', productCode] : ['login'];
  }

  private logout(returnUrl: string[], timeout: boolean): Observable<any> {
    this.activityMonitorService.stopMonitoring();

    // Without a user token, the user is essentially already logged out. Exit here.
    if (!this.apiGatewayService.userToken) {
      return of(returnUrl);
    }

    let fullRedirectUrl = `${environment.okta.clientConfig.redirectUri}/${location.pathname}#/logout`.replace('//all', '/all');
    if (returnUrl[1]) {
      fullRedirectUrl = `${fullRedirectUrl}/${returnUrl[1]}`;
    }
    if (timeout) {
      fullRedirectUrl = `${fullRedirectUrl}?timeout=true`;
    }

    return forkJoin([this.logoutEca(), this.logoutOkta(fullRedirectUrl)]).pipe(mapTo(returnUrl));
  }

  private logoutEca() {
    const apiConfig = this.apiGatewayService.makeApiConfigString();
    const url = `eca-security-service/${apiConfig}/v0/logout`;

    return this.appContextService.getToken().pipe(
      switchMap(token => this.apiGatewayService.post<any>(url, { token: token })),
      catchError((err) => {
        console.error(err);
        return of(undefined);
      })
    );
  }

  private async logoutOkta(redirectUrl: string) {
    try {
      await this.oktaService.logout(redirectUrl);
    } catch (err) {
      // ignore errors
      console.error(err);
    }
  }

  private navigate(url: string[], timeout: boolean) {
    this.zone.run(() => {
      if (timeout) {
        this.router.navigate(url, { queryParams: { timeout: true } });
      } else {
        this.router.navigate(url);
      }
    })
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
  }
}
